import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { unwrap } from '../common/apiCommons';
import axios from 'axios';

export interface ProjectRecordDto {
  id: number;
  employee: {
    id: number;
    name?: string;
    user?: { first_name: string; last_name: string };
  };
  project: { id: number; name: string };
  start: number;
  end: number;
  duration: number;
  comment: string;
  factor_calculated: number | null;
  factor_aggregated: number | null;
  seen: boolean;
}

export interface ProjectRecordSummaryRow {
  project_id: number;
  project_name: string;
  record_count: number;
  total_duration_seconds: number;
  avg_duration_seconds: number;
  avg_factor_calculated: number | null;
  avg_factor_aggregated: number | null;
}

export interface EmployeeDuration {
  employee_id: number;
  employee_name: string;
  total_duration_seconds: number;
}

export interface GlobalSummaryProject {
  project_id: number;
  project_name: string;
  total_duration_seconds: number;
  avg_factor_calculated: number | null;
  employees: EmployeeDuration[];
}

export interface GlobalSummaryResponse {
  by_project: GlobalSummaryProject[];
}

export interface BreakdownEmployee {
  employee_id: number;
  employee_name: string;
  total_duration_seconds: number;
  avg_factor_calculated: number | null;
  record_count: number;
}

export interface BreakdownFactorTrend {
  year: number;
  week: number;
  avg_factor_calculated: number | null;
  record_count: number;
}

export interface ProjectBreakdownResponse {
  by_employee: BreakdownEmployee[];
  factor_trend: BreakdownFactorTrend[];
  calculated_project_time_seconds: number | null;
  project_time_seconds: number;
  total_duration_seconds: number;
  record_count: number;
}

export interface ListParams {
  start: string;
  end: string;
  only_unseen?: boolean;
  project?: number;
  employee?: number;
}

const queries = {
  async list(params: ListParams) {
    const res = await axios.post<ProjectRecordDto[]>('/projects/records/', {
      start: params.start,
      end: params.end,
      only_unseen: params.only_unseen ?? false,
      project: params.project ?? null,
      employee: params.employee ?? null,
    });
    return unwrap(res);
  },
  async summary(start: string, end: string) {
    const res = await axios.post<ProjectRecordSummaryRow[]>(
      '/projects/records/summary/',
      { start, end },
    );
    return unwrap(res);
  },
  async globalSummary(start: string, end: string) {
    const res = await axios.post<GlobalSummaryResponse>(
      '/projects/records/global-summary/',
      { start, end },
    );
    return unwrap(res);
  },
  async projectBreakdown(start: string, end: string, project: number) {
    const res = await axios.post<ProjectBreakdownResponse>(
      '/projects/records/project-breakdown/',
      { start, end, project },
    );
    return unwrap(res);
  },
};

const mutations = {
  async markSeen(recordId: number) {
    const res = await axios.patch<ProjectRecordDto>(
      `/projects/records/${recordId}/seen/`,
    );
    return unwrap(res);
  },
};

const hooks = {
  useProjectRecords(
    params: ListParams,
    options?: UseQueryOptions<ProjectRecordDto[]>,
  ) {
    return useQuery({
      queryKey: ['ProjectRecord', 'list', params],
      queryFn: () => queries.list(params),
      ...(options ?? {}),
    });
  },
  useProjectRecordsSummary(
    start: string,
    end: string,
    options?: UseQueryOptions<ProjectRecordSummaryRow[]>,
  ) {
    return useQuery({
      queryKey: ['ProjectRecord', 'summary', start, end],
      queryFn: () => queries.summary(start, end),
      ...(options ?? {}),
    });
  },
  useGlobalSummary(
    start: string,
    end: string,
    options?: UseQueryOptions<GlobalSummaryResponse>,
  ) {
    return useQuery({
      queryKey: ['ProjectRecord', 'globalSummary', start, end],
      queryFn: () => queries.globalSummary(start, end),
      ...(options ?? {}),
    });
  },
  useProjectBreakdown(
    start: string,
    end: string,
    project: number | null,
    options?: UseQueryOptions<ProjectBreakdownResponse>,
  ) {
    return useQuery({
      queryKey: ['ProjectRecord', 'breakdown', start, end, project],
      queryFn: () => queries.projectBreakdown(start, end, project!),
      enabled: project != null,
      ...(options ?? {}),
    });
  },
  useMarkProjectRecordSeen() {
    const queryClient = useQueryClient();
    return useMutation(
      (recordId: number) => mutations.markSeen(recordId),
      {
        async onSuccess() {
          await queryClient.invalidateQueries({ queryKey: ['ProjectRecord'] });
        },
      },
    );
  },
};

const ProjectRecordApi = { queries, mutations, hooks };
export default ProjectRecordApi;
